/* eslint-disable max-lines */
import cn from 'classnames';
import { useState, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { PriorityBadge } from './priority-badge';
import { useSetItemPriority } from '@utils/api/back-office/priority-queue/use-set-item-priority';
import { useBulkSetPriority } from '@utils/api/back-office/priority-queue/use-bulk-set-priority';
import { PRIORITY_CLIFF } from '@utils/types/back-office/priority-queue';

const PRESETS = [100, 150, 200] as const;

/**
 * `datetime-local` yields naive local wall-clock ("2026-07-29T00:00") with no
 * zone. Sent as-is the backend parses it naive and Postgres resolves it against
 * the *server* session timezone, so "today only" picked at local midnight lands
 * hours off. Anchor it to the operator's actual instant before sending.
 */
function toIsoInstant(localValue: string): string | undefined {
  if (!localValue) return undefined;
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export type SetPriorityTarget =
  | {
      mode: 'single';
      itemId: string;
      currentPriority: number;
      zoomId?: string;
    }
  | {
      mode: 'bulk-ids';
      itemIds: number[];
      orgName?: string;
    }
  | {
      mode: 'bulk-org';
      organizationId: number;
      organizationName: string;
      queuedCount: number;
      tsAfter?: string;
      tsBefore?: string;
    };

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  target: SetPriorityTarget | null;
};

export function SetPriorityDialog({
  isOpen,
  onClose,
  onSuccess,
  target
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const reasonId = useId();

  const [newPriority, setNewPriority] = useState<number | ''>(150);
  const [reason, setReason] = useState('');
  const [confirmDeprioritise, setConfirmDeprioritise] = useState(false);
  const [includeFairUse, setIncludeFairUse] = useState(false);
  const [tsAfter, setTsAfter] = useState('');
  const [tsBefore, setTsBefore] = useState('');

  const { mutate: setItemPriority, isLoading: isSingle } = useSetItemPriority();
  const { mutate: bulkSetPriority, isLoading: isBulk } = useBulkSetPriority();
  const isLoading = isSingle || isBulk;

  useEffect(() => {
    if (!isOpen) {
      setNewPriority(150);
      setReason('');
      setConfirmDeprioritise(false);
      setIncludeFairUse(false);
      setTsAfter('');
      setTsBefore('');
    }
    if (isOpen && target?.mode === 'bulk-org') {
      setTsAfter(target.tsAfter ?? '');
      setTsBefore(target.tsBefore ?? '');
    }
  }, [isOpen, target]);

  const numPriority = typeof newPriority === 'number' ? newPriority : 0;
  const isAboveCliff = numPriority > PRIORITY_CLIFF;
  const reasonMissing = reason.trim() === '';
  const cliffBlocksSubmit = isAboveCliff && !confirmDeprioritise;
  const canSubmit =
    !reasonMissing &&
    !cliffBlocksSubmit &&
    typeof newPriority === 'number' &&
    newPriority >= 1 &&
    newPriority <= 1000;

  const handleConfirm = () => {
    if (!target || !canSubmit) return;
    const org_priority = numPriority;
    const confirm_deprioritise = isAboveCliff ? confirmDeprioritise : undefined;

    if (target.mode === 'single') {
      setItemPriority(
        {
          itemId: target.itemId,
          payload: { org_priority, reason, confirm_deprioritise }
        },
        {
          onSuccess: () => {
            onClose();
            onSuccess();
          }
        }
      );
    } else if (target.mode === 'bulk-ids') {
      bulkSetPriority(
        {
          org_priority,
          reason,
          confirm_deprioritise,
          item_ids: target.itemIds,
          include_fair_use: includeFairUse || undefined
        },
        {
          onSuccess: () => {
            onClose();
            onSuccess();
          }
        }
      );
    } else {
      bulkSetPriority(
        {
          org_priority,
          reason,
          confirm_deprioritise,
          organization_id: target.organizationId,
          include_fair_use: includeFairUse || undefined,
          session_timestamp_after: toIsoInstant(tsAfter),
          session_timestamp_before: toIsoInstant(tsBefore)
        },
        {
          onSuccess: () => {
            onClose();
            onSuccess();
          }
        }
      );
    }
  };

  const title =
    target?.mode === 'single' ? t('set-priority') : t('bulk-set-priority');

  const currentPriority =
    target?.mode === 'single' ? target.currentPriority : null;

  const orgName =
    target?.mode === 'bulk-ids'
      ? target.orgName
      : target?.mode === 'bulk-org'
        ? target.organizationName
        : undefined;

  const queuedCount =
    target?.mode === 'bulk-org' ? target.queuedCount : undefined;

  const bodyBg = getColour('secondLayer');
  const borderColor = getColour('border');
  const normalText = getColour('normalText');
  /* eslint-disable-next-line no-restricted-syntax -- Semantic accent colour for active input */
  const accentBlue = '#5794f6';
  /* eslint-disable-next-line no-restricted-syntax -- Semantic warning colour for cliff */
  const warningAmber = '#f59e0b';
  /* eslint-disable-next-line no-restricted-syntax -- Semantic error colour for invalid input */
  const errorRed = '#ef4444';

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleConfirm}
      confirmDisabled={!canSubmit || isLoading}
      title={title}
      size="medium"
      confirmLabel={isLoading ? '...' : t('confirm')}
      cancelLabel={t('cancel')}
      destructive={false}
      autoFocusConfirm={false}
    >
      <div className="space-y-4 text-sm" style={{ color: normalText }}>
        {/* Context */}
        {target?.mode === 'single' && target.zoomId && (
          <div className="text-xs opacity-60 font-mono truncate">
            {target.zoomId}
          </div>
        )}

        {/* Current → new */}
        <div className="flex items-center gap-3 flex-wrap">
          {currentPriority !== null && (
            <>
              <div>
                <div className="text-xs opacity-60 mb-1">
                  {t('current-priority')}
                </div>
                <PriorityBadge priority={currentPriority} />
              </div>
              <span className="opacity-40 text-lg">→</span>
            </>
          )}
          <div>
            <div className="text-xs opacity-60 mb-1">{t('new-priority')}</div>
            {typeof newPriority === 'number' ? (
              <PriorityBadge priority={numPriority} />
            ) : (
              <span className="text-xs opacity-40">{t('enter-priority')}</span>
            )}
          </div>
        </div>

        {/* Preset buttons */}
        <div className="flex gap-2 flex-wrap">
          {PRESETS.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => setNewPriority(p)}
              className={cn(
                'px-3 py-1.5 rounded-2xl border text-xs font-semibold transition',
                newPriority === p
                  ? 'border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400'
                  : 'hover:opacity-80'
              )}
              style={
                newPriority !== p
                  ? { borderColor, color: normalText }
                  : undefined
              }
            >
              {p}
            </button>
          ))}
          <input
            type="number"
            min={1}
            max={1000}
            value={newPriority}
            onChange={e => {
              const v = e.target.value;
              setNewPriority(
                v === '' ? '' : Math.min(1000, Math.max(1, Number(v)))
              );
            }}
            placeholder={t('custom')}
            className="w-24 h-8 px-2.5 rounded-2xl border text-xs outline-none"
            style={{
              borderColor:
                newPriority !== 100 &&
                newPriority !== 150 &&
                newPriority !== 200
                  ? accentBlue
                  : borderColor,
              backgroundColor: bodyBg,
              color: normalText
            }}
          />
        </div>

        {/* Cliff warning */}
        {isAboveCliff && (
          <div
            className="p-3 rounded-2xl border space-y-2"
            style={{
              borderColor: warningAmber,
              backgroundColor: 'rgba(245,158,11,0.08)'
            }}
          >
            <div className="font-semibold text-amber-600 dark:text-amber-400 text-xs">
              {t('cliff-warning', {
                priority: numPriority,
                cliff: PRIORITY_CLIFF
              })}
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={confirmDeprioritise}
                onChange={e => setConfirmDeprioritise(e.target.checked)}
                className="accent-amber-500"
              />
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                {t('confirm-deprioritise')}
              </span>
            </label>
          </div>
        )}

        {/* Reason */}
        <div>
          <label
            htmlFor={reasonId}
            className="block text-xs font-semibold mb-1 opacity-80"
          >
            {t('priority-reason')}
            <span className="text-red-500 ml-0.5">*</span>
          </label>
          <input
            id={reasonId}
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder={t('priority-reason-placeholder')}
            className="w-full h-9 px-3 rounded-2xl border outline-none text-xs"
            style={{
              borderColor: reasonMissing ? errorRed : borderColor,
              backgroundColor: bodyBg,
              color: normalText
            }}
          />
          {reasonMissing && (
            <p className="text-xs text-red-500 mt-1">
              {t('priority-reason-required')}
            </p>
          )}
        </div>

        {/* Bulk-org: apply-to-all copy */}
        {target?.mode === 'bulk-org' && queuedCount !== undefined && (
          <div
            className="p-3 rounded-2xl border text-xs space-y-3"
            style={{ borderColor, backgroundColor: bodyBg }}
          >
            <div className="font-semibold">
              {t('apply-to-all-queued-for-org', {
                count: queuedCount,
                orgName
              })}
            </div>

            {/* Date range narrowing */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="opacity-60 mb-1">
                  {t('session-timestamp-after')}
                </div>
                <input
                  type="datetime-local"
                  value={tsAfter}
                  onChange={e => setTsAfter(e.target.value)}
                  className="w-full h-8 px-2 rounded-2xl border outline-none text-xs"
                  style={{
                    borderColor,
                    backgroundColor: bodyBg,
                    color: normalText
                  }}
                />
              </div>
              <div>
                <div className="opacity-60 mb-1">
                  {t('session-timestamp-before')}
                </div>
                <input
                  type="datetime-local"
                  value={tsBefore}
                  onChange={e => setTsBefore(e.target.value)}
                  className="w-full h-8 px-2 rounded-2xl border outline-none text-xs"
                  style={{
                    borderColor,
                    backgroundColor: bodyBg,
                    color: normalText
                  }}
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeFairUse}
                onChange={e => setIncludeFairUse(e.target.checked)}
              />
              <span>{t('include-fair-use-sessions')}</span>
            </label>
          </div>
        )}

        {/* Guardrail note: this does NOT change org default */}
        <p className="text-xs opacity-50 italic">
          {t('set-priority-queued-only-note')}
        </p>
      </div>
    </ConfirmationModal>
  );
}
